import { useState } from 'react';
import { Brain, Plus, Settings, BarChart3, Zap } from 'lucide-react';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useProcessingRules } from '@/hooks/useProcessingRules';
import { PageHeader } from '@/components/PageHeader';
import { AIModelManager } from '@/components/AIModelManager';
import { RealTimeMonitor } from '@/components/RealTimeMonitor';
import { AdvancedAnalytics } from '@/components/AdvancedAnalytics';
import { PredictiveAnalytics } from '@/components/PredictiveAnalytics';

export default function AIParser() {
  const { rules, isLoading } = useProcessingRules();
  const [selectedTab, setSelectedTab] = useState('rules');

  return (
    <ModuleAccessGuard moduleId="bill_management">
      <AIParserContent rules={rules} isLoading={isLoading} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
    </ModuleAccessGuard>
  );
}

function AIParserContent({ rules, isLoading, selectedTab, setSelectedTab }: {
  rules: any[];
  isLoading: boolean;
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
}) {

  const rulesByType = {
    validation: rules.filter(rule => rule.rule_type === 'validation'),
    extraction: rules.filter(rule => rule.rule_type === 'extraction'),
    classification: rules.filter(rule => rule.rule_type === 'classification'),
    anomaly_detection: rules.filter(rule => rule.rule_type === 'anomaly_detection'),
  };

  const stats = [
    {
      title: "Active Rules",
      value: rules.filter(rule => rule.is_active).length,
      description: "Currently processing bills",
      icon: Zap,
      change: "+12%"
    },
    {
      title: "Validation Rules",
      value: rulesByType.validation.length,
      description: "Data quality checks",
      icon: Settings,
      change: "+5%"
    },
    {
      title: "Extraction Rules",
      value: rulesByType.extraction.length,
      description: "Field extraction logic",
      icon: Brain,
      change: "+8%"
    },
    {
      title: "Avg Accuracy",
      value: "94.2%",
      description: "AI processing accuracy",
      icon: BarChart3,
      change: "+2.1%"
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Parser"
        description="Configure and manage AI-powered bill processing rules"
        action={
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            New Rule
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
              <div className="flex items-center mt-2">
                <Badge variant="secondary" className="text-xs">
                  {stat.change}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="rules">Processing Rules</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="models">AI Models</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(rulesByType).map(([type, typeRules]) => (
              <Card key={type}>
                <CardHeader>
                  <CardTitle className="capitalize flex items-center gap-2">
                    {type.replace('_', ' ')} Rules
                    <Badge variant="outline">{typeRules.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    {type === 'validation' && 'Rules for validating extracted data quality'}
                    {type === 'extraction' && 'Rules for extracting specific fields from bills'}
                    {type === 'classification' && 'Rules for categorizing bills by type'}
                    {type === 'anomaly_detection' && 'Rules for detecting unusual patterns'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {typeRules.length === 0 ? (
                    <div className="text-center py-8">
                      <Brain className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No {type.replace('_', ' ')} rules configured</p>
                      <Button variant="outline" size="sm" className="mt-2">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Rule
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {typeRules.slice(0, 3).map((rule) => (
                        <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            {rule.description && (
                              <p className="text-sm text-muted-foreground">{rule.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={rule.is_active ? "default" : "secondary"}>
                              {rule.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <Badge variant="outline">P{rule.priority}</Badge>
                          </div>
                        </div>
                      ))}
                      {typeRules.length > 3 && (
                        <Button variant="ghost" size="sm" className="w-full">
                          View All {typeRules.length} Rules
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Processing Templates</CardTitle>
              <CardDescription>
                Pre-configured templates for different bill types and utility companies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Brain className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Templates Coming Soon</h3>
                <p className="text-muted-foreground mb-4">
                  Pre-built templates for common utility bill formats will be available here
                </p>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="models" className="space-y-6">
          <AIModelManager />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <Tabs defaultValue="realtime" className="space-y-4">
            <TabsList>
              <TabsTrigger value="realtime">Real-Time Monitor</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Analytics</TabsTrigger>
              <TabsTrigger value="predictive">Predictive Analytics</TabsTrigger>
            </TabsList>
            
            <TabsContent value="realtime">
              <RealTimeMonitor />
            </TabsContent>
            
            <TabsContent value="advanced">
              <AdvancedAnalytics />
            </TabsContent>
            
            <TabsContent value="predictive">
              <PredictiveAnalytics />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}